import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Download, RotateCw } from 'lucide-react';

interface ProgressStep {
  step: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  message: string;
}

interface LocalProjectBundle {
  siteId: string;
  suggestedRoot: string;
  files: { relPath: string; content: string }[];
}

interface StatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'dead';
  progress: ProgressStep[];
  result: {
    siteId: string;
    domain: string;
    apiDomain: string;
    localProjectBundle?: LocalProjectBundle | null;
  } | null;
  error: string | null;
}

async function downloadProjectBundle(bundle: LocalProjectBundle): Promise<void> {
  // JSZip is a chunky dep but only loaded on this admin-only page when the
  // admin actually clicks the button — the dynamic import keeps it out of the
  // main bundle.
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  for (const file of bundle.files) {
    zip.file(file.relPath, file.content);
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${bundle.siteId}-project.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function SiteWizardProgress() {
  const { jobId } = useParams<{ jobId: string }>();
  const [data, setData] = useState<StatusResponse | null>(null);
  const [pollError, setPollError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');

  useEffect(() => {
    if (!jobId) return;

    let active = true;
    const poll = async () => {
      try {
        const result = await api.get<StatusResponse>(`/admin/platform/site-wizard/status/${jobId}`);
        if (active) {
          setData(result);
          setPollError('');
          // Stop polling when done
          if (result.status === 'completed' || result.status === 'failed' || result.status === 'dead') return;
        }
      } catch (err: any) {
        if (active) setPollError(err.message || 'Failed to fetch status');
      }
      // Continue polling
      if (active) setTimeout(poll, 2000);
    };

    poll();
    return () => { active = false; };
  }, [jobId, retryNonce]);

  async function handleRetry() {
    if (!jobId) return;
    setRetrying(true);
    setRetryError('');
    try {
      await api.post(`/admin/platform/site-wizard/retry/${jobId}`, {});
      setRetryNonce((n) => n + 1);
    } catch (err: any) {
      setRetryError(err.message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  if (pollError && !data) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="text-red-600 mb-4">{pollError}</p>
        <Link to="/admin/settings" className="text-sage-600 hover:underline">Back to Settings</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-sage-600" />
        <p className="text-text-secondary mt-2">Loading...</p>
      </div>
    );
  }

  const isComplete = data.status === 'completed';
  const isFailed = data.status === 'failed' || data.status === 'dead';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <Link to="/admin/settings" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </Link>
        <h1 className="text-2xl font-display font-bold text-text-primary">
          {isComplete ? 'Site Created!' : isFailed ? 'Site Creation Failed' : 'Creating Site...'}
        </h1>
        {!isComplete && !isFailed && (
          <p className="text-sm text-text-secondary mt-1">This may take a few minutes. You can leave this page and come back.</p>
        )}
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
        {data.progress.map((step) => (
          <div key={step.step} className="flex items-center gap-3 px-5 py-3">
            {step.status === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />}
            {step.status === 'failed' && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
            {step.status === 'running' && <Loader2 className="w-5 h-5 text-sage-600 animate-spin flex-shrink-0" />}
            {step.status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-stone-200 flex-shrink-0" />}
            <span className={`text-sm ${step.status === 'running' ? 'text-text-primary font-medium' : step.status === 'done' ? 'text-text-secondary' : step.status === 'failed' ? 'text-red-600' : 'text-text-muted'}`}>
              {step.message}
            </span>
          </div>
        ))}
      </div>

      {/* Error */}
      {data.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {data.error}
        </div>
      )}

      {/* Retry */}
      {isFailed && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-sage-600 hover:bg-sage-700 disabled:bg-sage-400 text-white text-sm font-medium px-4 py-2 transition-colors self-start"
          >
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
            {retrying ? 'Retrying…' : 'Retry failed steps'}
          </button>
          <p className="text-xs text-text-muted">
            Completed AI steps are cached and will be skipped — only failed steps re-run.
          </p>
          {retryError && <p className="text-xs text-red-600">{retryError}</p>}
        </div>
      )}

      {/* Success */}
      {isComplete && data.result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold text-green-800">Site Ready!</h2>
          <div className="text-sm text-green-700 space-y-1">
            <p><strong>Site ID:</strong> {data.result.siteId}</p>
            <p><strong>Domain:</strong> {data.result.domain}</p>
            <p><strong>API:</strong> {data.result.apiDomain}</p>
          </div>
          <div className="bg-green-100 rounded-lg p-4 text-xs font-mono text-green-800 space-y-1">
            <p>Dev backend: SITE_ID={data.result.siteId} cd functions && bun run dev</p>
            <p>Dev frontend: SITE_ID={data.result.siteId} cd web && npm run dev</p>
            <p>Deploy: ./scripts/deploy-site.sh {data.result.siteId}</p>
          </div>
          {data.result.localProjectBundle && (
            <LocalProjectDownload bundle={data.result.localProjectBundle} />
          )}
        </div>
      )}
    </div>
  );
}

function LocalProjectDownload({ bundle }: { bundle: LocalProjectBundle }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  // The server's suggestedRoot is `/opt` when the wizard runs in the Linux
  // container — irrelevant for a Windows admin reading this page. Sniff the
  // browser's OS so the displayed path matches where the admin will actually
  // drop the zip.
  const isWindows = typeof navigator !== 'undefined' && /Win/i.test(navigator.userAgent);
  const localRoot = isWindows ? 'C:/knxprojects' : '~/projects';
  const targetPath = `${localRoot}/${bundle.siteId}/`;

  async function handleClick() {
    setError('');
    setDownloading(true);
    try {
      await downloadProjectBundle(bundle);
    } catch (e: any) {
      setError(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="border-t border-green-200 pt-4 space-y-2">
      <h3 className="text-sm font-semibold text-green-800">Local project bundle</h3>
      <p className="text-xs text-green-700">
        Download and unzip into <code className="font-mono bg-green-100 px-1 rounded">{targetPath}</code> to give Claude Code a per-site project directory with site-specific CLAUDE.md.
      </p>
      <button
        type="button"
        onClick={handleClick}
        disabled={downloading}
        className="inline-flex items-center gap-2 rounded-md bg-green-700 hover:bg-green-800 disabled:bg-green-400 text-white text-sm font-medium px-3 py-1.5 transition-colors"
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {downloading ? 'Building zip…' : `Download ${bundle.siteId}-project.zip`}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
